// Utilities for reading SSE throttle configuration
// Preferred usage: import { getSseThrottleMs, DEFAULT_SSE_THROTTLE_MS, MIN_SSE_THROTTLE_MS } from './sseThrottle'

/**
 * Default SSE throttle in milliseconds
 * @typedef {number} DEFAULT_SSE_THROTTLE_MS
 */
export const DEFAULT_SSE_THROTTLE_MS = 250;

/**
 * Minimum allowed throttle to avoid extreme render storms
 * @typedef {number} MIN_SSE_THROTTLE_MS
 */
export const MIN_SSE_THROTTLE_MS = 50;

/**
 * Get the SSE throttle value in milliseconds from environment variable REACT_APP_SSE_THROTTLE_MS
 * Falls back to DEFAULT_SSE_THROTTLE_MS if not set or invalid
 * @link typedef {number} SSEThrottleMs
 * @link typedef {number} DEFAULT_SSE_THROTTLE_MS
 * @link typedef {number} MIN_SSE_THROTTLE_MS
 * @returns {MIN_SSE_THROTTLE_MS || DEFAULT_SSE_THROTTLE_MS} Throttle value in milliseconds
 * @linkref documents/DEV-TOOLS.md SSE Throttling Documentation
 */
export function getSseThrottleMs() {
  // Read safely: process may not exist in browser bundles; also support window or window.__env
  const envRaw =
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SSE_THROTTLE_MS) ||
    (typeof window !== 'undefined' &&
      (window.REACT_APP_SSE_THROTTLE_MS ||
        (window.__env && window.__env.REACT_APP_SSE_THROTTLE_MS)));

  const parsed = envRaw ? parseInt(envRaw, 10) : NaN;
  if (envRaw && (!Number.isFinite(parsed) || parsed < MIN_SSE_THROTTLE_MS)) {
    // Avoid noisy console.warn in production; only warn in dev
    if (process && process.env && process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Invalid REACT_APP_SSE_THROTTLE_MS value, falling back to default', envRaw);
    }
  }

  return Number.isFinite(parsed) && parsed >= MIN_SSE_THROTTLE_MS
    ? parsed
    : DEFAULT_SSE_THROTTLE_MS;
}
